import React, { useCallback, useMemo, memo } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '@lib/components/Modal/Modal';
import Table from '@lib/components/Table/Table';
import { useTableItemsBulkSelect } from '@lib/hooks/useTableItemsBulkSelect';
import { ModalMode } from '@lib/components/Modal/enums';
import Search from '@lib/components/Search/Search';
import { BaseItem, Column } from '@lib/components/Table/types';
import {
  OrderingState,
  PaginationState,
  SearchStringState,
} from '@lib/enums/urls';
import ModalTableEmptyState from './ModalTableEmptyState';
import getTableActionsForSelected from './utils/getTableActionsForSelected';
import styles from './ModalTableMultiselect.module.scss';

interface ModalTableMultiunselectProps<T extends BaseItem> {
  caption?: string;
  columns: Column<T>[];
  dataSelected: T[];
  isOpen: boolean;
  loadingSelected?: boolean;
  firstLoadingSelected?: boolean;
  onClose: () => void;
  onSelect: (values: string[]) => void;
  onSelectedIdsChange?: (ids: string[]) => void;
  orderingStateSelected: OrderingState;
  paginationStateSelected: PaginationState;
  searchPlaceholder: string;
  searchStateSelected: SearchStringState;
  selected?: string[];
  totalEntriesSelected: number;
}

function ModalTableMultiunselect<T extends BaseItem>({
  caption = '',
  columns,
  dataSelected,
  firstLoadingSelected,
  isOpen,
  loadingSelected = false,
  onClose,
  onSelect,
  onSelectedIdsChange,
  orderingStateSelected,
  paginationStateSelected,
  searchPlaceholder,
  searchStateSelected,
  selected,
  totalEntriesSelected,
}: ModalTableMultiunselectProps<T>) {
  const { t } = useTranslation();

  const [searchValueSelected, onSearchHandlerSelected] = searchStateSelected;

  const useBulkSelectResult = useTableItemsBulkSelect<T>();
  const {
    handleUnselectAllTableItems,
    selectedTableItems,
    unselectedTableItems,
    isAllTableItemsSelected,
  } = useBulkSelectResult;

  const idsMarkedForRemoval = useMemo(() => {
    if (isAllTableItemsSelected) {
      if (!selected || selected.length === 0) {
        return [];
      }
      const uncheckedIds = new Set(
        unselectedTableItems.map((item) => String(item.id)),
      );
      return selected.filter((id) => !uncheckedIds.has(id));
    }

    return selectedTableItems.map((item) => String(item.id));
  }, [
    isAllTableItemsSelected,
    selected,
    selectedTableItems,
    unselectedTableItems,
  ]);

  const handleRemoveSelected = () => {
    // Remove checked items from the final selection
    // Calculate new IDs: original selected - checked items
    const checkedIds = idsMarkedForRemoval;
    const newSelectedIds = (selected || []).filter(
      (id) => !checkedIds.includes(id),
    );

    // Call onSelect with the updated selection
    onSelect(newSelectedIds);

    if (onSelectedIdsChange) {
      onSelectedIdsChange(newSelectedIds);
    }

    // Clear the bulk selection state
    handleUnselectAllTableItems();
  };

  // Actions for selected items
  const selectedItemsCount = idsMarkedForRemoval.length;
  const tableActionsForSelected = getTableActionsForSelected({
    selectedItemsCount,
    onRemoveSelected: handleRemoveSelected,
    t,
  });

  // Add a "Task" column as the first column
  const displayColumns = [
    {
      Header: t('task'),
      accessorFn: ({ item }: { item: T }) => {
        const paginationParams = paginationStateSelected[0];
        const currentPage = Number(paginationParams?.page || 1);
        const perPage = Number(paginationParams?.per_page || 10);
        const itemIndex = dataSelected.findIndex((d) => d.id === item.id);
        const orderNumber = (currentPage - 1) * perPage + itemIndex + 1;
        return `${t('task')} ${orderNumber}`;
      },
      width: 120,
    },
    ...columns,
  ];

  const handleModalClose = useCallback(() => {
    // Clear selections when modal closes without confirming
    handleUnselectAllTableItems();
    onClose();
  }, [handleUnselectAllTableItems, onClose]);

  const handleResetSearch = () => {
    onSearchHandlerSelected('');
  };

  const isContentVisible = !!totalEntriesSelected && !!dataSelected.length;
  const isEmptyStateVisible = !isContentVisible && !firstLoadingSelected;
  const isSearchApplied = !!searchValueSelected;
  const isEmptyState =
    !loadingSelected && isEmptyStateVisible && !isSearchApplied;
  const isEmptyStateBySearch = isEmptyStateVisible && isSearchApplied;
  const emptyState = (
    <ModalTableEmptyState
      isEmptyState={isEmptyState}
      isEmptyStateBySearch={isEmptyStateBySearch}
      isEmptyStateByFilter={false}
      onResetFilter={() => {
        return undefined;
      }}
      onResetSearch={handleResetSearch}
    />
  );

  const displayCounterText = t('task-plural', {
    count: selected?.length || 0,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleModalClose}
      caption={caption}
      mode={ModalMode.FullScreen}
      modalContentClassName={styles.modalContentClassName}
    >
      <div>
        <Search
          placeholder={searchPlaceholder}
          useSearchParam={false}
          value={searchValueSelected}
          onChange={onSearchHandlerSelected}
        />
        <Table<T>
          actionsForSelected={tableActionsForSelected}
          columns={displayColumns}
          counterText={displayCounterText}
          data={dataSelected}
          emptyState={emptyState}
          hasSelectColumn
          loading={loadingSelected}
          orderingState={orderingStateSelected}
          paginationState={paginationStateSelected}
          totalEntries={totalEntriesSelected}
          useBulkSelectResult={useBulkSelectResult}
        />
      </div>
    </Modal>
  );
}

export default memo(ModalTableMultiunselect) as <T extends BaseItem>(
  props: ModalTableMultiunselectProps<T>,
) => React.ReactElement;
